import React, { useState, useEffect } from 'react';
import { Card } from '@smartphone-tycoon/shared-ui';
import type { ActiveEvent } from '@/types/dynamicEvents';

interface SystemicEventNotificationProps {
  event: ActiveEvent;
  onDismiss: () => void;
  duration?: number; // auto-dismiss duration in ms
}

export function SystemicEventNotification({ 
  event, 
  onDismiss, 
  duration = 8000 
}: SystemicEventNotificationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (duration / 100));
        if (newProgress <= 0) {
          setIsVisible(false);
          setTimeout(onDismiss, 300); // Allow fade out animation
          return 0;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [duration, onDismiss]);

  if (!isVisible) return null;

  const { event: dynamicEvent } = event;

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-theme-bg-secondary border-theme-error',
          text: 'text-theme-text-primary',
          icon: '🚨',
          progress: 'bg-theme-error'
        };
      case 'high':
        return {
          bg: 'bg-theme-bg-secondary border-theme-warning',
          text: 'text-theme-text-primary',
          icon: '⚠️',
          progress: 'bg-theme-warning'
        };
      case 'medium':
        return {
          bg: 'bg-theme-bg-secondary border-theme-warning',
          text: 'text-theme-text-primary',
          icon: '📊',
          progress: 'bg-theme-warning'
        };
      case 'low':
        return {
          bg: 'bg-theme-bg-secondary border-theme-accent',
          text: 'text-theme-text-primary',
          icon: 'ℹ️',
          progress: 'bg-theme-accent'
        };
      default:
        return {
          bg: 'bg-theme-bg-secondary border-theme-border',
          text: 'text-theme-text-primary',
          icon: '📢',
          progress: 'bg-theme-text-muted'
        };
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'economic': return '💹';
      case 'technology': return '🔬';
      case 'competition': return '🏢';
      case 'regulation': return '⚖️';
      case 'social': return '👥';
      default: return '📢';
    }
  };

  const styles = getSeverityStyles(dynamicEvent.severity);

  return (
    <div className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
    }`}>
      <Card className={`max-w-md shadow-lg ${styles.bg} border-l-4`}>
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className="text-lg">{getCategoryIcon(dynamicEvent.category)}</span>
              <span className="text-lg">{styles.icon}</span>
              <h3 className={`font-semibold ${styles.text}`}>
                Market Event
              </h3>
            </div>
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(onDismiss, 300);
              }}
              className={`${styles.text} hover:opacity-70 transition-opacity`}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="mb-3">
            <h4 className={`font-medium ${styles.text} mb-1`}>
              {dynamicEvent.title}
            </h4>
            <p className="text-sm text-theme-text-secondary leading-relaxed">
              {dynamicEvent.description}
            </p>
          </div>

          {/* News Headline */}
          {dynamicEvent.newsHeadline && (
            <div className="mb-3 p-2 bg-theme-bg-content rounded border">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-xs font-medium text-theme-text-muted uppercase tracking-wide">
                  📰 Breaking News
                </span>
              </div>
              <p className="text-sm font-medium text-theme-text-primary">
                {dynamicEvent.newsHeadline}
              </p>
            </div>
          )}

          {/* Market Effects Preview */}
          {event.outcome?.marketModifiers && event.outcome.marketModifiers.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-theme-text-secondary mb-1">
                Market Impact:
              </div>
              <div className="space-y-1">
                {event.outcome.marketModifiers.slice(0, 2).map((modifier, index) => (
                  <div key={index} className="text-xs text-theme-text-secondary flex items-center space-x-2">
                    <span className="w-2 h-2 bg-theme-text-muted rounded-full"></span>
                    <span>
                      {modifier.field.replace(/([A-Z])/g, ' $1').toLowerCase()}: 
                      {modifier.type === 'multiplier' ? 
                        ` ${(modifier.value * 100).toFixed(0)}%` : 
                        ` ${modifier.value > 0 ? '+' : ''}${modifier.value}`
                      }
                      {modifier.duration && ` (${modifier.duration}mo)`}
                    </span>
                  </div>
                ))}
                {event.outcome.marketModifiers.length > 2 && (
                  <div className="text-xs text-theme-text-muted">
                    +{event.outcome.marketModifiers.length - 2} more effects...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Meta info */}
          <div className="flex items-center justify-between text-xs text-theme-text-muted">
            <div className="flex items-center space-x-2">
              <span className="capitalize">{dynamicEvent.category}</span>
              {dynamicEvent.historical && (
                <>
                  <span>•</span>
                  <span>Historical</span>
                </>
              )}
              {dynamicEvent.region && (
                <>
                  <span>•</span>
                  <span>{dynamicEvent.region}</span>
                </>
              )}
            </div>
            <span>
              {new Date(event.triggeredAt).toLocaleTimeString()}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full bg-theme-bg-tertiary rounded-full h-1">
              <div 
                className={`h-1 rounded-full transition-all duration-100 ${styles.progress}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Container component for managing multiple notifications
interface SystemicEventNotificationsProps {
  events: ActiveEvent[];
  onDismiss: (eventId: string) => void;
}

export function SystemicEventNotifications({ 
  events, 
  onDismiss 
}: SystemicEventNotificationsProps) {
  const systemicEvents = (events || []).filter(e => 
    e.event.type === 'systemic' && e.status === 'active'
  );

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3">
      {systemicEvents.map((event, index) => (
        <div
          key={event.event.id}
          style={{ 
            transform: `translateY(${index * 10}px)`,
            zIndex: 50 - index 
          }}
        >
          <SystemicEventNotification
            event={event}
            onDismiss={() => onDismiss(event.event.id)}
            duration={8000 + index * 1000} // Stagger dismissal
          />
        </div>
      ))}
    </div>
  );
}